import { useSelector } from "react-redux";
import { FaLocationDot, FaBuilding } from "react-icons/fa6";
import { MdAccessTime, MdClose, MdLink } from "react-icons/md";
import { useState, useMemo } from "react";
import Avatar from '@mui/material/Avatar';
import AvatarGroup from '@mui/material/AvatarGroup';
import { deepOrange, deepPurple, green, pink, blue } from '@mui/material/colors';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

const ProfessionalEvents = () => {
    const UserEvent = useSelector((state) => state?.calender?.professionalEventsData) || [];
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [timeRange, setTimeRange] = useState('past');

    // Filter events based on selected time range
    const filteredEvents = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const twelveMonthsAgo = new Date(today);
        twelveMonthsAgo.setMonth(today.getMonth() - 12);
        
        const twelveMonthsLater = new Date(today);
        twelveMonthsLater.setMonth(today.getMonth() + 12);
        
        if (timeRange === 'past') {
            return UserEvent.filter(event => {
                const eventDate = new Date(event.startTime);
                eventDate.setHours(0, 0, 0, 0);
                return eventDate >= twelveMonthsAgo && eventDate < today;
            });
        } else {
            return UserEvent.filter(event => {
                const eventDate = new Date(event.startTime);
                eventDate.setHours(0, 0, 0, 0);
                return eventDate >= today && eventDate <= twelveMonthsLater;
            });
        }
    }, [UserEvent, timeRange]);

    console.log("Filtered Events:", filteredEvents);

    // Array of colors for avatars
    const avatarColors = [deepOrange[500], deepPurple[500], green[500], pink[500], blue[500]];

    // Company logo colors for default logos
    const companyLogoColors = ['#0044C6', '#4CAF50', '#FF9800', '#E91E63', '#9C27B0', '#00BCD4'];

    // Helper function to get initials from name
    const getInitials = (name) => {
        if (!name) return '?';
        return name
            .split(' ')
            .map(word => word[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    // Helper function to get company initials for default logo
    const getCompanyInitials = (companyName) => {
        if (!companyName) return 'C';
        return companyName
            .split(' ')
            .map(word => word[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    // Helper function to format date
    const formatEventDate = (dateString) => {
        const date = new Date(dateString);
        const options = { 
            weekday: 'short', 
            day: 'numeric', 
            month: 'short', 
            year: 'numeric' 
        };
        return date.toLocaleDateString('en-US', options).replace(/,/g, '');
    };

    // Helper function to format full date and time for drawer
    const formatFullDateTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            day: 'numeric', 
            month: 'short', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true 
        });
    };

    // Helper function to format time
    const formatTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true 
        });
    };

    // Handle event click
    const handleEventClick = (event) => {
        setSelectedEvent(event);
        setDrawerOpen(true);
    };

    // Handle drawer close
    const handleDrawerClose = () => {
        setDrawerOpen(false);
        setTimeout(() => setSelectedEvent(null), 300);
    };

    // Handle location click - opens in Google Maps
    const handleLocationClick = (location) => {
        if (location) {
            const encodedLocation = encodeURIComponent(location);
            window.open(`https://www.google.com/maps/search/?api=1&query=${encodedLocation}`, '_blank');
        }
    };

    // Handle meet link click
    const handleMeetLinkClick = (meetLink) => {
        if (meetLink) {
            window.open(meetLink, '_blank', 'noopener,noreferrer');
        }
    };

    // Handle time range change
    const handleTimeRangeChange = (event, newTimeRange) => {
        if (newTimeRange !== null) {
            setTimeRange(newTimeRange);
        }
    };

    // Group events by month and year
    const groupEventsByMonth = (events) => {
        const grouped = {};
        
        events.forEach(event => {
            const date = new Date(event.startTime);
            const monthYear = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
            
            if (!grouped[monthYear]) {
                grouped[monthYear] = [];
            }
            grouped[monthYear].push(event);
        });
        
        return grouped;
    };

    // Sort events by date based on time range
    const sortEventsByDate = (events) => {
        return [...events].sort((a, b) => {
            if (timeRange === 'past') {
                return new Date(b.startTime) - new Date(a.startTime);
            } else {
                return new Date(a.startTime) - new Date(b.startTime);
            }
        });
    };

    const groupedEvents = groupEventsByMonth(filteredEvents);

    // Sort months based on time range
    const sortedMonths = Object.entries(groupedEvents).sort(([monthYearA], [monthYearB]) => {
        const dateA = new Date(monthYearA);
        const dateB = new Date(monthYearB);
        if (timeRange === 'past') {
            return dateB - dateA;
        } else {
            return dateA - dateB;
        }
    });

    // Get current date range for display
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const twelveMonthsAgo = new Date(today);
    twelveMonthsAgo.setMonth(today.getMonth() - 12);
    const twelveMonthsLater = new Date(today);
    twelveMonthsLater.setMonth(today.getMonth() + 12);
    
    const dateRangeText = timeRange === 'past' 
        ? `${twelveMonthsAgo.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} - ${today.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`
        : `${today.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} - ${twelveMonthsLater.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`;

    return (
        <>
            <div className="p-4 max-w-[1400px] mx-auto w-full">
                <div className="flex justify-between items-center mb-5 border-b-2 border-[#0044C6] pb-2.5 flex-wrap gap-3">
                    <h1 className="text-2xl text-gray-800 font-semibold m-0">
                        Professional Events
                    </h1>
                    
                    <div className="flex gap-3 items-center flex-wrap">
                        <ToggleButtonGroup
                            value={timeRange}
                            exclusive
                            onChange={handleTimeRangeChange}
                            aria-label="time range"
                            size="small"
                            sx={{
                                '& .MuiToggleButton-root': {
                                    fontSize: '12px',
                                    padding: '4px 12px',
                                    fontWeight: 500
                                },
                                '& .Mui-selected': {
                                    backgroundColor: '#0044C6 !important',
                                    color: 'white !important',
                                    '&:hover': {
                                        backgroundColor: '#0033a0 !important'
                                    }
                                }
                            }}
                        >
                            <ToggleButton value="past" aria-label="last 12 months">
                                Last 12 Months
                            </ToggleButton>
                            <ToggleButton value="upcoming" aria-label="upcoming 12 months">
                                Upcoming 12 Months
                            </ToggleButton>
                        </ToggleButtonGroup>
                        
                        <Chip 
                            label={timeRange === 'past' ? `Past: ${dateRangeText}` : `Upcoming: ${dateRangeText}`}
                            sx={{ 
                                backgroundColor: '#E3F2FD',
                                color: '#0044C6',
                                fontWeight: 500,
                                fontSize: '14px'
                            }} 
                        />
                    </div>
                </div>
                
                {sortedMonths?.length === 0 ? (
                    <div className="text-center py-8 px-4 bg-gray-50 rounded-xl">
                        <p className="text-xl text-gray-600 mb-1">
                            No events found in the {timeRange === 'past' ? 'last 12 months' : 'upcoming 12 months'}
                        </p>
                        <p className="text-sm text-gray-400">
                            Events from {dateRangeText} will appear here
                        </p>
                    </div>
                ) : (
                    sortedMonths?.map(([monthYear, events]) => (
                        <div key={monthYear} className="mb-[30px]">
                            <h2 className="text-xl mb-3.5 text-[#0044C6] bg-blue-50 px-4 py-2 rounded-md font-medium border-l-4 border-l-[#0044C6] flex justify-between items-center">
                                <span>{monthYear} <span className="text-gray-500 text-sm ml-2">({events.length})</span></span>
                                <Chip 
                                    label={timeRange === 'past' ? "Latest First" : "Earliest First"}
                                    size="small"
                                    sx={{ 
                                        backgroundColor: '#0044C6',
                                        color: 'white',
                                        fontWeight: 500,
                                        fontSize: '11px',
                                        height: '24px'
                                    }} 
                                />
                            </h2>
                            
                            <div className="grid grid-cols-3 gap-3 w-full">
                                {sortEventsByDate(events).map(event => {
                                    const eventDate = new Date(event.startTime);
                                    eventDate.setHours(0, 0, 0, 0);
                                    const isToday = eventDate.getTime() === today.getTime();
                                    
                                    return (
                                        <div 
                                            key={event.id} 
                                            onClick={() => handleEventClick(event)}
                                            className="bg-white border border-gray-100 rounded-lg p-3 shadow-sm transition-all duration-200 cursor-pointer flex flex-col h-auto min-h-[160px] max-h-[220px] relative hover:shadow-[0_4px_8px_rgba(0,68,198,0.15)] hover:border-[#0044C6] hover:-translate-y-0.5"
                                        >
                                            {/* Today Badge for upcoming events */}
                                            {timeRange === 'upcoming' && isToday && (
                                                <Chip 
                                                    label="Today" 
                                                    size="small"
                                                    sx={{ 
                                                        backgroundColor: '#FF9800',
                                                        color: 'white',
                                                        fontWeight: 600,
                                                        fontSize: '9px',
                                                        height: '18px',
                                                        position: 'absolute',
                                                        top: '8px',
                                                        right: '8px'
                                                    }} 
                                                />
                                            )}
                                            
                                            {/* Company Name and Logo Section - Main Title */}
                                            <div className="flex items-center gap-2 mb-2 border-b border-gray-50 pb-1.5">
                                                {/* Company Logo */}
                                                <Avatar
                                                    src={event.companyDetails?.companyLogo}
                                                    sx={{ 
                                                        width: 28, 
                                                        height: 28,
                                                        bgcolor: event.companyDetails?.companyLogo ? 'transparent' : companyLogoColors[Math.floor(Math.random() * companyLogoColors.length)],
                                                        fontSize: '12px',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    {!event.companyDetails?.companyLogo && (
                                                        event.companyDetails?.companyName ? 
                                                            getCompanyInitials(event.companyDetails.companyName) : 
                                                            <FaBuilding size={14} />
                                                    )}
                                                </Avatar>
                                                
                                                {/* Company Name */}
                                                <Typography sx={{
                                                    fontSize: '14px',
                                                    fontWeight: 600,
                                                    color: '#0044C6',
                                                    flex: 1,
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}>
                                                    {event.companyDetails?.companyName || 'Professional Event'}
                                                </Typography>
                                            </div>

                                            <div className={`text-[12px] font-medium mb-1.5 px-2 py-0.5 rounded-xl inline-block self-start ${
                                                isToday && timeRange === 'upcoming' 
                                                    ? 'text-amber-600 bg-amber-50' 
                                                    : 'text-[#0044C6] bg-blue-50'
                                            }`}>
                                                {formatEventDate(event.startTime)}
                                            </div>
                                            
                                            <div className="text-[15px] font-semibold mb-1.5 text-gray-800 leading-tight whitespace-nowrap overflow-hidden text-ellipsis">
                                                {event.title || 'Untitled Event'}
                                            </div>
                                            
                                            <div className="text-[11px] text-gray-500 flex items-center gap-1 mb-1.5 flex-wrap">
                                                <span className="bg-gray-100 px-2 py-0.5 rounded-xl text-gray-600 flex items-center gap-0.5 text-[11px]">
                                                    <span className="text-[10px]"><MdAccessTime/></span> {formatTime(event.startTime)}
                                                </span>
                                                <span className="text-gray-400">-</span>
                                                <span className="bg-gray-100 px-2 py-0.5 rounded-xl text-gray-600 text-[11px]">
                                                    {formatTime(event.endTime)}
                                                </span>
                                            </div>
                                            
                                            {event.location && (
                                                <div 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleLocationClick(event.location);
                                                    }}
                                                    className="mb-1 text-[11px] text-gray-500 flex items-center gap-1 cursor-pointer"
                                                    title="Click to open in Google Maps"
                                                >
                                                    <span className="text-[10px]"><FaLocationDot color="#0044C6"/></span> 
                                                    <span className="bg-gray-100 px-2 py-0.5 rounded-xl text-[11px] whitespace-nowrap overflow-hidden text-ellipsis max-w-[120px] text-[#0044C6] underline decoration-[#0044C6] underline-offset-2">
                                                        {event.location}
                                                    </span>
                                                </div>
                                            )}
                                            
                                            {/* Attendees Avatar Group */}
                                            {event.attendees && event.attendees.length > 0 && (
                                                <div className="mt-auto mb-1 flex items-center justify-between pt-2 border-t border-gray-50">
                                                    <span className="text-[11px] text-gray-500 flex items-center gap-1">
                                                        <span>👥</span>
                                                        <span className="bg-[#0044C6] text-white rounded-[10px] px-1.5 py-0.5 text-[10px] font-medium">
                                                            {event.attendees.length}
                                                        </span>
                                                    </span>
                                                    
                                                    <AvatarGroup 
                                                        max={4} 
                                                        spacing="small"
                                                        sx={{
                                                            '& .MuiAvatar-root': {
                                                                width: 28,
                                                                height: 28,
                                                                fontSize: '12px',
                                                                fontWeight: 600,
                                                                border: '2px solid white',
                                                                transition: 'transform 0.2s',
                                                                '&:hover': {
                                                                    transform: 'scale(1.1)',
                                                                    zIndex: 10
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        {event.attendees.map((attendee, index) => (
                                                            <Avatar
                                                                key={attendee.email || index}
                                                                alt={attendee.name || 'Attendee'}
                                                                src={attendee.profile_pic}
                                                                sx={{ 
                                                                    bgcolor: attendee.profile_pic ? 'transparent' : avatarColors[index % avatarColors.length],
                                                                    color: attendee.profile_pic ? 'inherit' : 'white'
                                                                }}
                                                            >
                                                                {!attendee.profile_pic && getInitials(attendee.name)}
                                                            </Avatar>
                                                        ))}
                                                    </AvatarGroup>
                                                </div>
                                            )}
                                            
                                            {event.description && !event.attendees && (
                                                <div className="mt-auto text-[11px] text-gray-500 pt-1.5 mt-1 leading-tight line-clamp-1">
                                                    {event.description}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Event Details Drawer - MUI Drawer remains */}
            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={handleDrawerClose}
                PaperProps={{
                    sx: {
                        width: '400px',
                        maxWidth: '90vw',
                        borderTopLeftRadius: '16px',
                        borderBottomLeftRadius: '16px',
                        padding: '24px'
                    }
                }}
            >
                {selectedEvent && (
                    <Box>
                        {/* Header with close button */}
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            mb: 3
                        }}>
                            <Typography variant="h5" sx={{ 
                                fontWeight: 600,
                                color: '#0044C6',
                                fontSize: '1.5rem'
                            }}>
                                Event Details
                            </Typography>
                            <IconButton onClick={handleDrawerClose} sx={{ 
                                backgroundColor: '#f5f5f5',
                                '&:hover': { backgroundColor: '#e0e0e0' }
                            }}>
                                <MdClose />
                            </IconButton>
                        </Box>

                        {/* Company Section - Main Title with Logo */}
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 2,
                            mb: 3,
                            pb: 2,
                            borderBottom: '2px solid #E3F2FD'
                        }}>
                            <Avatar
                                src={selectedEvent.companyDetails?.companyLogo}
                                sx={{ 
                                    width: 48, 
                                    height: 48,
                                    bgcolor: selectedEvent.companyDetails?.companyLogo ? 'transparent' : companyLogoColors[Math.floor(Math.random() * companyLogoColors.length)],
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                    border: '2px solid #0044C6'
                                }}
                            >
                                {!selectedEvent.companyDetails?.companyLogo && (
                                    selectedEvent.companyDetails?.companyName ? 
                                        getCompanyInitials(selectedEvent.companyDetails.companyName) : 
                                        <FaBuilding size={24} />
                                )}
                            </Avatar>
                            <Box>
                                <Typography variant="h5" sx={{ 
                                    fontWeight: 700, 
                                    color: '#0044C6',
                                    fontSize: '1.3rem',
                                    lineHeight: 1.2
                                }}>
                                    {selectedEvent.companyDetails?.companyName || 'Professional Event'}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Title */}
                        <Typography variant="h4" sx={{ 
                            fontWeight: 700, 
                            mb: 3,
                            fontSize: '1.8rem',
                            color: '#333',
                            lineHeight: 1.2
                        }}>
                            {selectedEvent.title || 'Untitled Event'}
                        </Typography>

                        {/* Event Type Chip */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                            <Chip 
                                label={selectedEvent.eventType || 'Event'} 
                                sx={{ 
                                    backgroundColor: '#E3F2FD',
                                    color: '#0044C6',
                                    fontWeight: 500
                                }} 
                            />
                            {selectedEvent.meetingType && (
                                <Chip 
                                    label={selectedEvent.meetingType}
                                    sx={{ 
                                        backgroundColor: '#FF9800',
                                        color: 'white',
                                        fontWeight: 500
                                    }} 
                                />
                            )}
                        </Box>

                        {/* Date and Time Section */}
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="subtitle1" sx={{ 
                                fontWeight: 600, 
                                color: '#666',
                                mb: 2,
                                fontSize: '1rem'
                            }}>
                                Date & Time
                            </Typography>
                            <Box sx={{ 
                                backgroundColor: '#f8f9fa',
                                p: 2,
                                borderRadius: '12px'
                            }}>
                                <Typography sx={{ fontWeight: 500, mb: 1 }}>
                                    {formatFullDateTime(selectedEvent.startTime)} - {formatTime(selectedEvent.endTime)}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Duration: {selectedEvent.duration} minutes
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                                    Timezone: {selectedEvent.timeZone || 'Asia/Kolkata'}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Location Section */}
                        {selectedEvent.location && (
                            <Box sx={{ mb: 4 }}>
                                <Typography variant="subtitle1" sx={{ 
                                    fontWeight: 600, 
                                    color: '#666',
                                    mb: 2,
                                    fontSize: '1rem'
                                }}>
                                    Location
                                </Typography>
                                <Box 
                                    onClick={() => handleLocationClick(selectedEvent.location)}
                                    sx={{ 
                                        backgroundColor: '#f8f9fa',
                                        p: 2,
                                        borderRadius: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        border: '1px solid transparent',
                                        '&:hover': {
                                            backgroundColor: '#e3f2fd',
                                            borderColor: '#0044C6',
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 4px 8px rgba(0, 68, 198, 0.15)'
                                        }
                                    }}
                                    title="Click to open in Google Maps"
                                >
                                    <FaLocationDot color="#0044C6" />
                                    <Typography sx={{ 
                                        color: '#0044C6',
                                        textDecoration: 'underline',
                                        textDecorationColor: '#0044C6',
                                        textUnderlineOffset: '2px'
                                    }}>
                                        {selectedEvent.location}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {/* Meet Link Section */}
                        {selectedEvent.meetLink && (
                            <Box sx={{ mb: 4 }}>
                                <Typography variant="subtitle1" sx={{ 
                                    fontWeight: 600, 
                                    color: '#666',
                                    mb: 2,
                                    fontSize: '1rem'
                                }}>
                                    Meeting Link
                                </Typography>
                                <Box 
                                    onClick={() => handleMeetLinkClick(selectedEvent.meetLink)}
                                    sx={{ 
                                        backgroundColor: '#f8f9fa',
                                        p: 2,
                                        borderRadius: '12px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        border: '1px solid transparent',
                                        '&:hover': {
                                            backgroundColor: '#e3f2fd',
                                            borderColor: '#0044C6',
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 4px 8px rgba(0, 68, 198, 0.15)'
                                        }
                                    }}
                                    title="Click to open meeting link"
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <MdLink color="#0044C6" />
                                        <Typography sx={{ 
                                            color: '#0044C6',
                                            fontWeight: 500
                                        }}>
                                            Join Meeting
                                        </Typography>
                                    </Box>
                                    <Typography 
                                        sx={{ 
                                            color: '#0044C6',
                                            textDecoration: 'underline',
                                            textDecorationColor: '#0044C6',
                                            textUnderlineOffset: '2px',
                                            wordBreak: 'break-all',
                                            fontSize: '0.9rem'
                                        }}
                                    >
                                        {selectedEvent.meetLink}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {/* Description Section */}
                        {selectedEvent.description && (
                            <Box sx={{ mb: 4 }}>
                                <Typography variant="subtitle1" sx={{ 
                                    fontWeight: 600, 
                                    color: '#666',
                                    mb: 2,
                                    fontSize: '1rem'
                                }}>
                                    Description
                                </Typography>
                                <Box sx={{ 
                                    backgroundColor: '#f8f9fa',
                                    p: 2,
                                    borderRadius: '12px'
                                }}>
                                    <Typography>{selectedEvent.description}</Typography>
                                </Box>
                            </Box>
                        )}

                        {/* Attendees Section */}
                        {selectedEvent.attendees && selectedEvent.attendees.length > 0 && (
                            <Box sx={{ mb: 4 }}>
                                <Typography variant="subtitle1" sx={{ 
                                    fontWeight: 600, 
                                    color: '#666',
                                    mb: 2,
                                    fontSize: '1rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}>
                                    <span>Attendees</span>
                                    <Chip 
                                        label={selectedEvent.attendees.length} 
                                        size="small"
                                        sx={{ 
                                            backgroundColor: '#0044C6',
                                            color: 'white',
                                            fontWeight: 500
                                        }} 
                                    />
                                </Typography>
                                <Box sx={{ 
                                    backgroundColor: '#f8f9fa',
                                    p: 2,
                                    borderRadius: '12px'
                                }}>
                                    {selectedEvent.attendees.map((attendee, index) => (
                                        <Box 
                                            key={attendee.email || index}
                                            sx={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: 2,
                                                mb: index < selectedEvent.attendees.length - 1 ? 2 : 0,
                                                pb: index < selectedEvent.attendees.length - 1 ? 2 : 0,
                                                borderBottom: index < selectedEvent.attendees.length - 1 ? '1px solid #e0e0e0' : 'none'
                                            }}
                                        >
                                            <Avatar
                                                src={attendee.profile_pic}
                                                sx={{ 
                                                    width: 40, 
                                                    height: 40,
                                                    bgcolor: attendee.profile_pic ? 'transparent' : avatarColors[index % avatarColors.length]
                                                }}
                                            >
                                                {!attendee.profile_pic && getInitials(attendee.name)}
                                            </Avatar>
                                            <Box>
                                                <Typography sx={{ fontWeight: 600 }}>
                                                    {attendee.name || 'Unknown'}
                                                </Typography>
                                                {attendee.email && (
                                                    <Typography variant="body2" color="textSecondary">
                                                        {attendee.email}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* Metadata Section */}
                        <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                            <Typography variant="caption" color="textSecondary" display="block">
                                Created by: {selectedEvent.createdBy || 'Unknown'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                                Created at: {new Date(selectedEvent.createdAt).toLocaleString()}
                            </Typography>
                            {selectedEvent.updatedBy && (
                                <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                                    Updated by: {selectedEvent.updatedBy}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}
            </Drawer>
        </>
    );
};

export default ProfessionalEvents;